import { createSexValidator, getSexDropdownSource } from '@/model/Animal.utils';
import { _isEmpty, _isNil, _isNotBlank, _isNotEmpty, _notNil } from '@/littledash.ts';
import { type SpeciesV1 } from '@/model/Species.model.ts';
import type { GroupApiId, TreatmentGroup } from '@/model/TreatmentGroup.model';
import { type MetadataField, type MetadataFieldTypes, type MetadataFieldWithValue } from '@/model/Metadata.model.ts';
import type Handsontable from 'handsontable';
import { DateUtils } from '@/utils/Date.utils';
import { isAfter } from 'date-fns';
import {
  type AnimalDefinitionApiId,
  type PlannedAnimal,
  type PlannedAnimalData,
  PlanTypeMap,
  type PlanTypes,
} from '@/model/PlannedAnimal.model.ts';
import { type Nullable, type UUID } from '@/model/Common.model.ts';
import { changeApiIdPrefix, generateTempApiId, isTempApiId } from '@/utils/ApiId.utils.ts';
import { type StrainApiId } from '@/model/Strain.model.ts';
import { type TempAnimalApiId, type TempAnimalDefinitionApiId } from '@/model/SamplePlan.model';
import { type AnimalApiId, type AnimalSpecies } from '@/model/Animal.model';
import { type PlannedAnimalDefinition } from '@/components/Animals/PlannedAnimals/PlannedAnimals.utils.tsx';
import type { CustomColumnSettings } from '@/components/UI/SpreadSheet/SpreadSheet.model';
import { requiredAsteriskColumnHeader } from '@/components/UI/SpreadSheet/SpreadSheet.utils';
import { MultiSelectEditor } from '@/components/UI/SpreadSheet/editors/MultiSelectEditor';
import { DateEditorWrapper } from '@/components/UI/SpreadSheet/editors/DateEditorWrapper';
import { readMultiSelectCellValue, writeMultiSelectCell } from '@/components/UI/SpreadSheet/multiSelectCellTransport';
import { defaultMetadataToColumnSettingMapper, formatColumnSettingsDataValue } from '@/support/hot';
import { updateStrainSelectDropdown } from '@/components/Colony/Colony.utils';
import { renderToStaticMarkup } from 'react-dom/server';
import { GroupCell, type GroupOption } from '@/components/Studies/Create/Steps/Animals/GroupCell';

export interface AnimalRow {
  apiId: AnimalDefinitionApiId | TempAnimalDefinitionApiId | AnimalApiId | TempAnimalApiId;
  plannedId: string;
  groupIdentifier: Nullable<UUID | GroupApiId>;
  sex: string;
  dob?: string;
  species?: string;
  strain?: string;
  metadata?: { [key: string]: MetadataFieldWithValue };
  // Handsontable stores metadata values under `meta[encodedTitle]` (flat key/value),
  // distinct from the v12-shaped `metadata[fieldId]` record. Populated only when the
  // pipeline is active and consumed by convertToPlannedAnimals.
  meta?: Record<string, unknown>;
  isMapped?: boolean;
}

export const createPlannedAnimalData = (
  animals: Array<PlannedAnimal>,
  planType: PlanTypes,
  animalsPerCage: number | null = null
): PlannedAnimalData => {
  switch (planType) {
    case PlanTypeMap.planned:
      return {
        type: planType,
        animals,
      };
    case PlanTypeMap.registered:
      return {
        type: planType,
        animals,
        animalsPerCage: animalsPerCage ?? 5,
      };
    case PlanTypeMap.skip:
      return {
        type: planType,
      };
  }
};

export const updateTmpIds = (animals: Array<AnimalRow>, planType: PlanTypes): Array<AnimalRow> => {
  return animals.map((animal) => ({
    ...animal,
    apiId: changeApiIdPrefix(animal.apiId, planType === PlanTypeMap.planned ? 'adf' : 'aml', true),
  }));
};

// ID and Sex are required in every plan type; Group and Species/Strain vary by flow, so
// `getColumnData` marks those.
export const baseColHeaders = {
  names: [
    requiredAsteriskColumnHeader('Planned animal ID'),
    'Group',
    requiredAsteriskColumnHeader('Sex'),
    'Species',
    'Strain',
  ],
  ids: ['plannedId', 'groupName', 'sex', 'species', 'strain'],
};

export const baseColHeadersWithDob = {
  names: [
    requiredAsteriskColumnHeader('Name'),
    'Group',
    requiredAsteriskColumnHeader('Sex'),
    requiredAsteriskColumnHeader('DOB'),
    'Species',
    'Strain',
  ],
  ids: ['plannedId', 'groupName', 'sex', 'dob', 'species', 'strain'],
};

export const baseColHeadersWithOptionalDob = {
  names: [
    requiredAsteriskColumnHeader('Name'),
    'Group',
    requiredAsteriskColumnHeader('Sex'),
    'DOB',
    'Species',
    'Strain',
  ],
  ids: ['plannedId', 'groupName', 'sex', 'dob', 'species', 'strain'],
};

export const validatePlannedAnimalId = (
  value: string,
  unselectedRows: Array<PlannedAnimalDefinition>,
  callback: (valid: boolean) => void,
  hot: Nullable<Handsontable.Core>,
  required: boolean = true,
  cache?: ValidationCache
) => {
  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  if ((!value || value.length < 1) && required) {
    return callback(false);
  }
  if (_notNil(cache)) {
    const count = (cache.idCounts.get(value) ?? 0) + unselectedRows.filter((row) => row.planned_id === value).length;
    return callback(count <= 1);
  }
  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  if (hot) {
    const currentData = hot.getData() as Array<Array<any>>;
    const idIndex = hot.propToCol('plannedId') as number;
    const duplicateIDs =
      currentData.filter((row) => _notNil(value) && row[idIndex] === value).length +
      unselectedRows.filter((row) => row.planned_id === value).length;
    return callback(duplicateIDs <= 1);
  }
  return callback(false);
};

export const validateGroupName = (
  value: string,
  unselectedRows: Array<PlannedAnimalDefinition>,
  callback: (valid: boolean) => void,
  hot: Handsontable | null,
  groups: Array<TreatmentGroup>,
  planType: PlanTypes,
  groupIdAccessor: (group: TreatmentGroup) => string,
  cache?: ValidationCache
) => {
  if (_isEmpty(value) && planType === PlanTypeMap.planned) {
    return callback(false);
  }
  const selectedGroup = groups.find((group) => groupIdAccessor(group) === value);
  if (_notNil(selectedGroup)) {
    if (_notNil(cache)) {
      const animalsInGroup =
        (selectedGroup.animals_count ?? 0) +
        (cache.groupCounts.get(value) ?? 0) +
        unselectedRows.filter((row) => row.study_group_api_id === selectedGroup.api_id).length;
      return callback(animalsInGroup <= selectedGroup.max_subjects);
    }
    if (_notNil(hot)) {
      const currentData = hot.getSourceData() as Array<AnimalRow>;
      const animalsInGroup = calculateAnimalsInGroup(selectedGroup, currentData, unselectedRows, groupIdAccessor);
      return callback(animalsInGroup <= selectedGroup.max_subjects);
    }
  }
  return callback(true);
};

export const validateSex = createSexValidator(true);

export const validateDob = (dobRequired: boolean, value: string, callback: (valid: boolean) => void) => {
  if (_isNotEmpty(value)) {
    const result = DateUtils.isValidDate(value) && !isAfter(new Date(value), new Date(DateUtils.dateNow()));
    return callback(result);
  }
  return callback(!dobRequired);
};

export const validateSpecies = (
  value: string,
  callback: (valid: boolean) => void,
  species: Array<SpeciesV1>,
  required: boolean = false
) => {
  if (_isNotEmpty(value)) {
    return callback(species.some((s) => s.name === value));
  }
  return callback(!required);
};

export const renderGroupContainer = (groups: Array<TreatmentGroup>, value: string | null) => {
  const selectedGroup = groups.find((group) => group.name === value) ?? '';
  const container = document.createElement('div');
  container.className = 'flex items-center pa2 justify-between';

  const contentContainer = document.createElement('div');
  contentContainer.className = 'flex items-center';

  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  if (value && selectedGroup) {
    const swatch = document.createElement('div');
    swatch.className = 'dib w2 h1 br1 mr2';
    swatch.style.background = selectedGroup?.color ?? '#000000';

    const text = document.createElement('span');
    text.textContent = selectedGroup?.name ?? '';
    contentContainer.appendChild(swatch);
    contentContainer.appendChild(text);
  }

  container.appendChild(contentContainer);
  return container;
};

export const handleMetadataChange = (
  metadata: Array<MetadataField>,
  studyMetadata: Array<MetadataField>,
  selectedMetadata: Array<MetadataField>,
  setSelectedMetadata: (metadata: Array<MetadataField>) => void,
  setAvailableMetadata: (metadata: Array<MetadataField>) => void
) => {
  const availableIds = new Set(metadata.map(({ id }) => id));
  const shownMetadata = selectedMetadata.reduce<Array<MetadataField>>((acc, shown) => {
    if (!availableIds.has(shown.id)) {
      acc.push(shown);
    }
    return acc;
  }, []);
  const shownIds = new Set(shownMetadata.map(({ id }) => id));
  studyMetadata.forEach((studyMeta) => {
    if (!availableIds.has(studyMeta.id) && !shownIds.has(studyMeta.id)) {
      shownMetadata.push(studyMeta);
    }
  });

  setSelectedMetadata(shownMetadata);
  setAvailableMetadata(metadata);
};

export type MetadataGlossaryItem = {
  id: number;
  api_id?: string;
  type: string;
  header?: string;
  title: string;
  field_type: MetadataFieldTypes;
  active?: boolean;
  in_use?: boolean;
  options?: Array<string>;
  read_only: boolean;
  slug: string;
  created_at?: string;
  required?: boolean;
  display_order?: number | null;
};

export const mapMetadataResponse = (metadata: Array<MetadataGlossaryItem>) => {
  return metadata.map((meta) => ({
    id: meta.id,
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    title: meta.title || '',
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    slug: meta.slug || '',
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    field_type: meta.field_type || 'text',
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    options: meta.options || [],
    active: meta.active !== undefined ? meta.active : true,
    glossary_id: meta.id,
    read_only: meta.read_only !== undefined ? meta.read_only : false,
    required: meta.required !== undefined ? meta.required : false,
    value: null,
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    glossary_api_id: meta.api_id || `mgl_${meta.id}`,
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    created_at: meta.created_at || new Date().toISOString(),
    display_order: meta.display_order,
  })) as Array<MetadataField>;
};

const calculateAnimalsInGroup = (
  group: TreatmentGroup,
  animalData: Array<AnimalRow>,
  unselectedRows: Array<PlannedAnimalDefinition>,
  groupIdAccessor: (group: TreatmentGroup) => string
) => {
  return (
    (group.animals_count ?? 0) +
    animalData.filter((row) => row.groupIdentifier === groupIdAccessor(group)).length +
    unselectedRows.filter((row) => row.study_group_api_id === group.api_id).length
  );
};

export const bannerMessageMap = {
  baseMessage: 'The table is pre-populated based on the total number of animals specified in Groups and Treatments.',
  conflicts: 'There are conflicts in the animal data due to group changes. Reset the table to align animal data.',
  availableCapacity: 'The total group capacity exceeds the number of animals. Reset the table to align animal data.',
} as const;
export type bannerMessageMap = (typeof bannerMessageMap)[keyof typeof bannerMessageMap];

export interface ValidationCache {
  idCounts: Map<string, number>;
  groupCounts: Map<string, number>;
}

export const buildValidationCache = (data: Array<AnimalRow>): ValidationCache => {
  const idCounts = new Map<string, number>();
  const groupCounts = new Map<string, number>();

  for (const row of data) {
    if (_isNotEmpty(row.plannedId)) {
      idCounts.set(row.plannedId, (idCounts.get(row.plannedId) ?? 0) + 1);
    }
    if (_notNil(row.groupIdentifier)) {
      const key = row.groupIdentifier as string;
      groupCounts.set(key, (groupCounts.get(key) ?? 0) + 1);
    }
  }

  return { idCounts, groupCounts };
};

export const findAffectedRows = (
  prevCache: ValidationCache,
  nextCache: ValidationCache,
  data: Array<AnimalRow>
): Array<number> => {
  const affectedIds = new Set<string>();

  for (const [id, count] of nextCache.idCounts) {
    if ((prevCache.idCounts.get(id) ?? 0) !== count) {
      affectedIds.add(id);
    }
  }
  for (const [id, count] of prevCache.idCounts) {
    if ((nextCache.idCounts.get(id) ?? 0) !== count) {
      affectedIds.add(id);
    }
  }

  const affectedGroups = new Set<string>();
  for (const [group, count] of nextCache.groupCounts) {
    if ((prevCache.groupCounts.get(group) ?? 0) !== count) {
      affectedGroups.add(group);
    }
  }
  for (const [group, count] of prevCache.groupCounts) {
    if ((nextCache.groupCounts.get(group) ?? 0) !== count) {
      affectedGroups.add(group);
    }
  }

  if (affectedIds.size === 0 && affectedGroups.size === 0) {
    return [];
  }

  const rows: Array<number> = [];
  for (let i = 0; i < data.length; i++) {
    const row = data[i];
    if (affectedIds.has(row.plannedId) || affectedGroups.has(row.groupIdentifier as string)) {
      rows.push(i);
    }
  }
  return rows;
};

const groupCellRenderer = (groupOptions: Map<string, GroupOption>) => {
  return (
    _instance: unknown,
    TD: HTMLTableCellElement,
    row: number,
    col: number,
    _prop: string | number,
    value: unknown
  ): void => {
    TD.innerHTML = '';
    const hot = _instance as { getCellMeta?: (r: number, c: number) => Record<string, unknown> };
    const cellMeta = hot.getCellMeta?.(row, col) ?? {};
    const readOnly = cellMeta.readOnly === true;
    TD.className = 'htMiddle';
    if (cellMeta.valid === false) {
      TD.classList.add('htInvalid');
    }
    if (readOnly) {
      TD.classList.add('htDimmed');
    }
    const container = document.createElement('div');
    container.classList.add('h-100', 'w-100');
    container.innerHTML = renderToStaticMarkup(
      <GroupCell option={groupOptions.get(((value as string) ?? '').trim())} readOnly={readOnly} />
    );
    TD.appendChild(container);
  };
};

interface SpeciesWithStrains {
  name: string;
  strains?: Array<{ name: string }> | null;
}

const strainOptionsBySpecies = (species: Array<SpeciesWithStrains>): Map<string, Array<string>> =>
  species.reduce<Map<string, Array<string>>>((acc, sp) => {
    const names = acc.get(sp.name) ?? [];
    (sp.strains ?? []).forEach((strain) => names.push(strain.name));
    acc.set(sp.name, names);
    return acc;
  }, new Map());

/** The select editor reads its options from cell meta, so row dependent strains are resolved here. */
export const createStrainCellsResolver = (species: Array<SpeciesWithStrains>): Handsontable.GridSettings['cells'] => {
  const optionsBySpecies = strainOptionsBySpecies(species);

  return function (this: { instance?: Handsontable.Core }, row: number, _column: number, prop?: string | number) {
    if (prop !== 'strain') {
      return {};
    }

    const hot = this?.instance;
    if (_isNil(hot)) {
      return {};
    }

    const rowData = hot.getSourceDataAtRow(row) as { species?: string } | null;

    return { options: optionsBySpecies.get(rowData?.species ?? '') ?? [] };
  } as Handsontable.GridSettings['cells'];
};

export const getBaseColumns = (
  groups: Array<TreatmentGroup>,
  groupIdAccessor: (group: TreatmentGroup) => string,
  species: Array<SpeciesV1>,
  includeDob: boolean = false,
  dobRequired: boolean = true,
  editGroups: boolean = true,
  planType: PlanTypes,
  unselectedRows: Array<PlannedAnimalDefinition> = [],
  includeUnknown: boolean = false,
  getValidationCache?: () => ValidationCache,
  speciesRequired: boolean = false
): Array<CustomColumnSettings> => {
  const strainOptions = strainOptionsBySpecies(species);
  const groupOptions = new Map<string, GroupOption>(
    groups.map((group) => {
      const value = groupIdAccessor(group);
      return [value, { value, label: group.name, color: group.color, no: group.no }];
    })
  );
  const groupOptionsComparator = (left: GroupOption, right: GroupOption): number => {
    const leftNo = left.no ?? Number.POSITIVE_INFINITY;
    const rightNo = right.no ?? Number.POSITIVE_INFINITY;
    return leftNo === rightNo ? 0 : leftNo - rightNo;
  };

  function plannedIdValidator(
    this: { instance?: Handsontable.Core },
    value: string,
    callback: (valid: boolean) => void
  ): void {
    validatePlannedAnimalId(
      value,
      unselectedRows,
      callback,
      (this.instance ?? null) as unknown as Parameters<typeof validatePlannedAnimalId>[3],
      true,
      getValidationCache?.()
    );
  }

  function groupValidator(
    this: { instance?: Handsontable.Core },
    value: string,
    callback: (valid: boolean) => void
  ): void {
    validateGroupName(
      value,
      unselectedRows.filter((a) => !_notNil(a.animal_api_id)),
      callback,
      (this.instance ?? null) as unknown as Parameters<typeof validateGroupName>[3],
      groups,
      planType,
      groupIdAccessor,
      getValidationCache?.()
    );
  }

  function strainValidator(this: unknown, value: string, callback: (valid: boolean) => void): void {
    const hot = (this as { instance?: Handsontable.Core }).instance;
    const row = (this as { row?: number }).row;

    if (!_notNil(hot) || !_notNil(row)) {
      return callback(true);
    }

    const speciesCol = hot.propToCol('species');
    const selectedSpecies = hot.getDataAtCell(row, speciesCol as number) as string;
    const hasSpecies = _notNil(selectedSpecies) && _isNotEmpty(selectedSpecies);
    const hasStrain = _notNil(value) && _isNotEmpty(value);

    if (!hasSpecies && !hasStrain) {
      return callback(!speciesRequired);
    }

    if (!hasSpecies && hasStrain) {
      return callback(false);
    }

    if (hasSpecies && !hasStrain) {
      return callback(false);
    }

    return callback((strainOptions.get(selectedSpecies) ?? []).includes(value));
  }

  const baseColumns: Array<CustomColumnSettings> = [
    {
      data: 'plannedId',
      type: 'text',
      readOnly: false,
      allowInvalid: true,
      width: 150,
      validator: plannedIdValidator,
      validationErrorMessage: () => 'ID is required and must be unique.',
    },
    {
      data: 'groupIdentifier',
      type: MultiSelectEditor.type,
      isMultiSelect: false,
      optionsMap: groupOptions,
      optionsComparator: groupOptionsComparator,
      readOnly: !editGroups,
      width: 200,
      validator: groupValidator,
      validationErrorMessage: (value: unknown) => {
        if (_isEmpty(value as string)) {
          return 'Planned animals must be assigned to a group';
        }
        const group = groups.find((g) => groupIdAccessor(g) === value);
        if (_notNil(group)) {
          if (editGroups) {
            return 'Number of planned animals assigned exceeds maximum subjects allowed: ' + (group.max_subjects ?? 0);
          }
          const availableCapacity = (group.max_subjects ?? 0) - (group.animals_count ?? 0);
          return 'Available group slots to register animals: ' + availableCapacity;
        }
        return 'Planned animals must be assigned to a group';
      },
      renderer: groupCellRenderer(groupOptions),
    },
    {
      data: 'sex',
      type: MultiSelectEditor.type,
      isMultiSelect: false,
      options: getSexDropdownSource(includeUnknown),
      allowInvalid: true,
      width: 110,
      validator: createSexValidator(includeUnknown),
    },
  ] as Array<CustomColumnSettings>;

  if (includeDob) {
    baseColumns.push({
      data: 'dob',
      type: DateEditorWrapper.type,
      width: 150,
      required: dobRequired,
      validator: function (value: string, callback: (valid: boolean) => void): void {
        validateDob(dobRequired, value, callback);
      },
      validationErrorMessage: () =>
        dobRequired
          ? 'Required in YYYY-MM-DD and must not be a future date.'
          : 'Must be in YYYY-MM-DD format and not a future date if provided.',
    } as CustomColumnSettings);
  }

  baseColumns.push(
    {
      data: 'species',
      type: MultiSelectEditor.type,
      isMultiSelect: false,
      width: 150,
      options: species.map((s) => s.name),
      validator: function (value: string, callback: (valid: boolean) => void): void {
        validateSpecies(value, callback, species, speciesRequired);
      },
      validationErrorMessage: (value: unknown) =>
        _isEmpty(value as string) ? 'Species is required.' : 'Is not a valid option',
      linkedValidationColumns: ['strain'],
    } as CustomColumnSettings,
    {
      data: 'strain',
      type: MultiSelectEditor.type,
      isMultiSelect: false,
      width: 150,
      validator: strainValidator,
      validationErrorMessage: (value: unknown) => {
        if (!speciesRequired) {
          return 'Strain is required when a Species is selected, otherwise it should be left blank.';
        }
        return _isEmpty(value as string)
          ? 'Strain is required.'
          : 'Strain must be a valid subtype of specified Species value';
      },
    } as CustomColumnSettings
  );

  return baseColumns;
};

export const getColumnData = (
  species: Array<SpeciesV1>,
  selectedMetadata: Array<MetadataField>,
  groups: Array<TreatmentGroup>,
  groupIdAccessor: (group: TreatmentGroup) => string,
  planType: PlanTypes,
  editGroups: boolean = true,
  dobRequired: boolean = true,
  unselectedRows: Array<PlannedAnimalDefinition> = [],
  includeUnknown: boolean = false,
  getValidationCache?: () => ValidationCache,
  speciesRequired: boolean = false
): {
  colHeaders: Array<string>;
  columns: Array<CustomColumnSettings>;
  cells: Handsontable.GridSettings['cells'];
} => {
  const includeDob = planType === PlanTypeMap.registered;
  const baseColumns = getBaseColumns(
    groups,
    groupIdAccessor,
    species,
    includeDob,
    dobRequired,
    editGroups,
    planType,
    unselectedRows,
    includeUnknown,
    getValidationCache,
    speciesRequired
  );

  const colHeaders = includeDob
    ? dobRequired
      ? baseColHeadersWithDob
      : baseColHeadersWithOptionalDob
    : baseColHeaders;

  // Group is only required while planning; Strain follows the Species flag.
  const markedColumnIds = new Set<string>([
    ...(planType === PlanTypeMap.planned ? ['groupName'] : []),
    ...(speciesRequired ? ['species', 'strain'] : []),
  ]);

  const headerNames =
    markedColumnIds.size === 0
      ? colHeaders.names
      : colHeaders.names.map((name, index) =>
          markedColumnIds.has(colHeaders.ids[index]) ? requiredAsteriskColumnHeader(name) : name
        );

  const metadataColumns = selectedMetadata.reduce<{
    headers: Array<string>;
    cols: Array<CustomColumnSettings>;
  }>(
    (acc, metadata) => {
      const { columnHeader, columnSettings } = defaultMetadataToColumnSettingMapper(metadata);
      acc.headers.push(metadata.required === true ? requiredAsteriskColumnHeader(columnHeader) : columnHeader);
      acc.cols.push(columnSettings as CustomColumnSettings);
      return acc;
    },
    { headers: [], cols: [] }
  );

  return {
    colHeaders: [...headerNames, ...metadataColumns.headers],
    columns: [...baseColumns, ...metadataColumns.cols],
    cells: createStrainCellsResolver(species),
  };
};

export const patchMetadata = (hotInstance: Handsontable.Core | null, species: Array<SpeciesV1>): void => {
  if (_notNil(hotInstance) && !(hotInstance as unknown as { isDestroyed: boolean }).isDestroyed) {
    const col = hotInstance.propToCol('species');
    const colData = hotInstance.getDataAtCol(col as number);
    colData.forEach((value: unknown, row: number) => {
      updateStrainSelectDropdown(hotInstance, species as unknown as Array<AnimalSpecies>, row, value as string);
    });
    hotInstance.render();
  }
};

const isMultiSelectField = (field: MetadataField): boolean =>
  field.field_type === 'multi_select' || field.field_type === 'lookup_multi_select';

/**
 * Build an empty `meta` record keyed by encoded titles so that Handsontable
 * column accessors (`meta.<encodedTitle>`) can read/write cell values without the row
 * being treated as missing a nested object.
 */
const buildEmptyMetaRecord = (selectedMetadata: Array<MetadataField>): Record<string, unknown> =>
  selectedMetadata.reduce<Record<string, unknown>>((acc, field) => {
    acc[formatColumnSettingsDataValue(field.title)] = '';
    return acc;
  }, {});

/**
 * Convert the v12-shaped `row.metadata[fieldId]` into the `row.meta[encodedTitle]`
 * representation so that a freshly rendered Handsontable instance can display the
 * existing values.
 */
export const metadataToMeta = (
  metadataRecord: Record<string, MetadataFieldWithValue> | undefined,
  selectedMetadata: Array<MetadataField>
): Record<string, unknown> => {
  return selectedMetadata.reduce<Record<string, unknown>>((acc, field) => {
    const encodedKey = formatColumnSettingsDataValue(field.title);
    const existing = metadataRecord?.[field.id] ?? metadataRecord?.[field.glossary_id as unknown as string | number];
    if (_notNil(existing) && _notNil(existing.value)) {
      acc[encodedKey] =
        isMultiSelectField(field) && Array.isArray(existing.value)
          ? writeMultiSelectCell(existing.value)
          : existing.value;
    } else {
      acc[encodedKey] = '';
    }
    return acc;
  }, {});
};

/**
 * Build the `meta` record for the metadata columns that are currently rendered, preferring the
 * value the row already carries - including one the user cleared - over the stored metadata, so
 * that rendering an extra column cannot roll a row back to its last saved values.
 */
export const mergeRowMetaForColumns = (
  row: Pick<AnimalRow, 'meta' | 'metadata'>,
  selectedMetadata: Array<MetadataField>
): Record<string, unknown> => {
  const storedMeta = metadataToMeta(row.metadata, selectedMetadata);
  const currentMeta = row.meta ?? {};
  return selectedMetadata.reduce<Record<string, unknown>>((acc, field) => {
    const encodedKey = formatColumnSettingsDataValue(field.title);
    acc[encodedKey] = Object.prototype.hasOwnProperty.call(currentMeta, encodedKey)
      ? currentMeta[encodedKey]
      : storedMeta[encodedKey];
    return acc;
  }, {});
};

/**
 * replacement for generateInitialAnimals. Seeds an empty `meta` bucket for
 * each selected metadata column so Handsontable writes land in a predictable path.
 */
export const generateInitialAnimals = (
  groups: Array<TreatmentGroup>,
  dobRequired: boolean,
  selectedMetadata: Array<MetadataField>
): Array<AnimalRow> => {
  const baseMeta = buildEmptyMetaRecord(selectedMetadata);
  const animalData: Array<AnimalRow> = [];
  const sortedGroups = [...groups].sort((a, b) => a.no - b.no);
  sortedGroups.forEach((group) => {
    const maxSubjects = group.max_subjects;
    if (maxSubjects > 0 && _isNotBlank(group.name ?? '')) {
      for (let i = 1; i <= maxSubjects; i++) {
        animalData.push({
          apiId: generateTempApiId('adf'),
          plannedId: `${group.no}_${i}`,
          groupIdentifier: group.temp_id as UUID,
          sex: '',
          dob: dobRequired ? DateUtils.dateNow() : '',
          species: '',
          strain: '',
          metadata: {},
          meta: { ...baseMeta },
        });
      }
    }
  });
  return animalData;
};

/**
 * replacement for generateAnimalColumns. Preserves v12 `metadata` (downstream
 * consumers still rely on it) and additionally populates `meta[encodedTitle]` so that
 * Handsontable renders the existing values.
 */
export const generateAnimalColumns = (
  animals: Array<PlannedAnimal>,
  groups: Array<TreatmentGroup>,
  planType: PlanTypes,
  dobRequired: boolean,
  selectedMetadata: Array<MetadataField>
): Array<AnimalRow> => {
  if (planType === PlanTypeMap.skip) {
    return [];
  }
  return animals.map((animal) => {
    const plannedGroup = groups.find((group) => group.temp_id === animal.group_temp_id);
    return {
      apiId:
        _notNil(animal.temp_id) && isTempApiId(planType === PlanTypeMap.planned ? 'adf' : 'aml', animal.temp_id)
          ? animal.temp_id
          : planType === PlanTypeMap.planned
            ? generateTempApiId('adf')
            : generateTempApiId('aml'),
      plannedId: animal.name,
      groupIdentifier: plannedGroup?.temp_id as UUID,
      sex: animal.sex,
      dob: animal?.dob ?? (dobRequired ? DateUtils.dateNow() : ''),
      species: animal.species,
      strain: animal.strain,
      metadata: animal.metadata ?? {},
      meta: metadataToMeta(animal.metadata, selectedMetadata),
    };
  });
};

/**
 * replacement for convertToPlannedAnimals. Reads metadata from
 * `row.meta[encodedTitle]` (where Handsontable writes) and rebuilds the v12-shaped
 * `metadata: Record<fieldId, MetadataFieldWithValue>` map expected by downstream
 * consumers (Redux store, study create POST payload, etc.).
 */
export const convertToPlannedAnimals = (
  animalData: Array<AnimalRow>,
  groups: Array<TreatmentGroup>,
  species: Array<SpeciesV1>,
  animalsPerCage: number | null,
  planType: PlanTypes,
  groupIdAccessor: (group: TreatmentGroup) => string,
  selectedMetadata: Array<MetadataField>
): PlannedAnimalData => {
  const groupMap = new Map(groups.map((g) => [groupIdAccessor(g), g]));
  const strainApiIdMap = new Map<string, string>();
  for (const s of species) {
    if (_isNotEmpty(s.strains)) {
      for (const strain of s.strains!) {
        strainApiIdMap.set(`${s.name}|${strain.name}`, strain.api_id);
      }
    }
  }

  const metadataFieldInfo = selectedMetadata.map((field) => ({
    field,
    encodedKey: formatColumnSettingsDataValue(field.title),
    isMultiSelect: isMultiSelectField(field),
  }));

  const plannedAnimals: Array<PlannedAnimal> = animalData.map((animal) => {
    const plannedGroup = groupMap.get(animal.groupIdentifier as string);
    const strainApiId = strainApiIdMap.get(`${animal.species}|${animal.strain}`) ?? '';

    const metaRecord = animal.meta ?? {};
    const existingMetadata = animal.metadata ?? {};
    const rebuiltMetadata: Record<string, MetadataFieldWithValue> = {};

    for (const { field, encodedKey, isMultiSelect } of metadataFieldInfo) {
      const existingEntry =
        existingMetadata[field.id] ?? existingMetadata[field.glossary_id as unknown as string | number];
      // A missing key means the column was never rendered, so keep the stored value.
      // A present key with nil/`''` is an explicit clear (Delete, Backspace, or the select X)
      // and must not be repopulated from previous metadata.
      const hasGridValue = Object.hasOwn(metaRecord, encodedKey);
      const rawValue = metaRecord[encodedKey];
      let value: MetadataFieldWithValue['value'];

      if (!hasGridValue) {
        value = existingEntry?.value ?? null;
      } else if (_isNil(rawValue)) {
        // Handsontable writes `null` when a cell is emptied with Delete. That is an explicit
        // clear rather than an absent value, so it must not fall back to the stored value.
        value = isMultiSelect ? [] : null;
      } else if (isMultiSelect && typeof rawValue === 'string') {
        value = readMultiSelectCellValue(rawValue);
      } else {
        value = rawValue as MetadataFieldWithValue['value'];
      }

      rebuiltMetadata[field.id] = {
        ...(existingEntry ?? {}),
        id: field.id,
        title: field.title,
        glossary_id: field.glossary_id,
        glossary_api_id: field.glossary_api_id,
        field_type: field.field_type,
        read_only: field.read_only,
        slug: field.slug,
        required: field.required,
        options: field.options,
        value,
      } as MetadataFieldWithValue;
    }

    Object.entries(existingMetadata).forEach(([key, entry]) => {
      if (!_notNil(rebuiltMetadata[key])) {
        rebuiltMetadata[key] = entry;
      }
    });

    return {
      temp_id:
        _notNil(animal.apiId) && isTempApiId(planType === PlanTypeMap.planned ? 'adf' : 'aml', animal.apiId)
          ? animal.apiId
          : planType === PlanTypeMap.planned
            ? generateTempApiId('adf')
            : generateTempApiId('aml'),
      name: animal.plannedId,
      group_temp_id: plannedGroup?.temp_id,
      sex: animal.sex,
      dob: animal?.dob ?? '',
      species: animal?.species,
      strain: animal?.strain,
      strainApiId: strainApiId as StrainApiId,
      metadata: rebuiltMetadata,
    };
  });

  return createPlannedAnimalData(plannedAnimals, planType, animalsPerCage);
};
